import React, {Component} from 'react';
import Resize from 'react-resize-detector';
import * as d3 from 'd3';
import _ from 'lodash';

import {hexbin} from 'd3-hexbin';
import shotUtils from '../utils/shot-utils';

import HoverOver from '../components/HoverOver.js';
import './HockeyShotChart.css';

const heatColors = [
  '#7a7a7a',
  '#c1c1c1',
  '#7ab2d7',
  '#2980b9'];

const darkerHeatColors = [
  '#676767',
  '#989898',
  '#67a9d4',
  '#0a6fb2'];

const hexWide = 18;
const hexagonRadius = 85 / (hexWide * 2 * .866);
const hexagonRadiusSizes = [hexagonRadius * .3, hexagonRadius * .45,
  hexagonRadius * .6, hexagonRadius * .75, hexagonRadius * .9];

function getNestedMeansThresholds(data, prop) {
  const values = _.map(data, prop ? prop : _.size);
  const mean = d3.mean(values);
  const min = d3.min(values);
  const max = d3.max(values);

  const lower = values.filter(function (i) {
    return i < mean;
  });

  const upper = values.filter(function (i) {
    return i > mean;
  });

  return [
    Math.max(2, min),
    Math.max(3, d3.mean(lower)),
    Math.max(4, mean),
    Math.max(5, d3.mean(upper)),
    Math.max(6, max)];
}

function transformHexbinData(data, visibilityThreshold = 0) {
  if (!data || !data.shots || !data.shots.length) {
    return '';
  }

  const binner = hexbin()
    .size([100, 85])
    .x(d => 42.5 + d.y)
    .y(d => 100 - d.x)
    .radius(hexagonRadius);

  // Bin to shots and filter out zones that dont meet the minimum threshold
  const hexbins = _.chain(binner(data.shots))
    .filter(function (bin) {
      return _.size(bin) > visibilityThreshold;
    })
    .orderBy('y')
    .orderBy('x')
    .value();

  const thresholdScale = d3.scaleThreshold()
    .domain(getNestedMeansThresholds(hexbins))
    .range(hexagonRadiusSizes);

  const color = d3.scaleThreshold()
    .domain([-5, 0, 5])
    .range(darkerHeatColors);

  // Return a map of the bins with additional data added
  return _.forEach(hexbins, function (bin) {
    bin.id = Math.round(bin.x) + '-' + Math.round(bin.y);

    // Count shots in bin to determine attempt count
    bin.type = 'bin';

    bin.attempted = _.size(bin);

    // Sum shots in bin that were made
    bin.made = +_.sumBy(bin, 'made');

    // Calculate the radius based on attempts
    bin.radius = thresholdScale(bin.attempted);

    // Calculate the hexbin path based on radius
    bin.hexbinPath = binner.hexagon(bin.radius);

    // Calculate the full hexbin path outline (for testing)
    bin.hexbinOutline = binner.hexagon(hexagonRadius);

    bin.shootingPct = bin.made / bin.attempted;

    bin.zone = shotUtils.getShotZone(bin);

    const offset = data.zones.diff[bin.zone];

    bin.desc = (
      <div>
        <h4>Shot Grouping</h4>
        <div><b>Shots</b> : <i>{`${bin.made} / ${bin.attempted}`}</i></div>
        <div><b>Percentage</b> : <i>{d3.format('.1p')(bin.shootingPct)}</i></div>
        <div><b>Zone</b> : <i>{offset}% {offset >= 0 ? 'Above' : 'Below'} Avg</i></div>
      </div>
    );

    bin.color = color(offset);
  });
}

class HockeyShotChart extends Component {

  constructor(props) {
    super(props);

    this.state = {
      width: 500,
      height: 250
    };
  }

  componentDidMount() {
    this.handelResize(this.refs.wrapper.offsetWidth, this.refs.wrapper.offsetHeight)
  }

  handelResize(width = 500) {
    const aspect = 1.2941;
    let w = width;
    let h = w * aspect;

    this.setState({width: w, height: h});
  }

  clearHover() {
    this.setState({hover: null});
  }

  createShots() {
    const self = this;
    const shots = _.sortBy(_.get(this.props, 'data.shots', []), 'made');

    const onHover = d => event => {
      const content = (
        <div>
          <h4>{d.made ? 'GOAL' : 'SHOT'}</h4>
          <div className="desc"><i>{d.desc}</i></div>
        </div>
      );
      const x = (43 + d.y) * self.state.width / 86;
      const y = (100.5 - d.x) * self.state.height / 110;

      self.setState({hover: {x, y, content}});
    };

    return (
      _.map(shots, d => {
        return <circle
          key={d.key}
          cx={42.5 + d.y}
          cy={100 - d.x}
          r={d.made ? .49 : .35}
          fill={d.made ? '#2b94d1' : '#bbb'}
          onClick={onHover(d)}
          onMouseEnter={onHover(d)}
          onMouseOut={self.clearHover.bind(self)}
        />
      })
    )
  }

  createBins() {
    const hexdata = transformHexbinData(this.props.data, this.props.shotsFiltered ? 1 : 0);
    const self = this;

    const onHover = d => event => {
      const content = d.desc;
      const x = (d.x + .5) * self.state.width / 86;
      const y = (d.y + .5) * self.state.height / 110;

      self.setState({hover: {x, y, content}});
    };

    return (
      _.map(hexdata, d => {
        return <path
          key={`${d.x}-${d.y}`}
          transform={`translate(${d.x},${d.y})`}
          d={d.hexbinPath}
          fill={d.color}
          opacity={.9}
          onClick={onHover(d)}
          onMouseEnter={onHover(d)}
          onMouseOut={self.clearHover.bind(self)}
        />
      })
    )
  }

  createCircles() {
    const hexdata = transformHexbinData(this.props.data, this.props.shotsFiltered ? 1 : 0);
    const self = this;

    const onHover = d => event => {
      const content = d.desc;
      const x = (d.x + .5) * self.state.width / 86;
      const y = (d.y + .5) * self.state.height / 110;

      self.setState({hover: {x, y, content}});
    };

    return (
      _.map(hexdata, d => {
        return <circle
          className="bin"
          key={`${d.x}-${d.y}`}
          cx={d.x}
          cy={d.y}
          r={d.radius * .866}
          fill={d.color}
          opacity={.9}
          onClick={onHover(d)}
          onMouseEnter={onHover(d)}
          onMouseOut={self.clearHover.bind(self)}
        />
      })
    )
  }

  createOutlines() {
    const hexdata = transformHexbinData(this.props.data);

    return (
      _.map(hexdata, d => {
        return <path
          key={`${d.x}-${d.y}`}
          transform={`translate(${d.x},${d.y})`}
          d={d.hexbinOutline}
          opacity={.2}
          fill={d.color}
        />
      })
    )
  }

  createZones(showStrokes) {
    const self = this;

    const color = d3.scaleThreshold()
      .domain([-5, 0, 5])
      .range(darkerHeatColors);

    const getColor = zone => {
      return color(this.props.data.zones.diff[zone]);
    };

    const getLabel = zone => {
      return this.props.data.zones.score[zone] || '';
    };

    const fillOpacity = showStrokes ? .55 : .2;
    const strokeWidth = showStrokes ? .4 : 0;

    const getHover = (zone, label, zx, zy) => event => {
      const shots = self.props.data.zones.score[zone];
      const percent = self.props.data.zones.pct[zone];
      const offset = self.props.data.zones.diff[zone];

      const content = (
        <div>
          <h4>{label}</h4>
          <div><b>Shots</b> : <i>{shots}</i></div>
          <div><b>Percentage</b> : <i>{percent}%</i></div>
          <div><b>Zone</b> : <i>{offset}% {offset >= 0 ? 'Above' : 'Below'} Avg</i></div>
        </div>
      );
      const x = zx * self.state.width / 86;
      const y = zy * self.state.height / 110 + 2;

      self.setState({hover: {x, y, content}});
    };

    return (
      <g>
        <path d="M0,0 L85,0 L85,11 L0,11 Z" fillOpacity={fillOpacity}
              fill={getColor('downLow')} stroke="#444" strokeWidth={strokeWidth}
              onMouseEnter={getHover('downLow', 'Down Low', 42.5, 5.5)}
              onClick={getHover('downLow', 'Down Low', 42.5, 5.5)}
              onMouseLeave={self.clearHover.bind(self)}/>

        <path d="M0,11 L0,31 L20.5,31 L31.5,11 Z" fillOpacity={fillOpacity}
              fill={getColor('leftLow')} stroke="#444" strokeWidth={strokeWidth}
              onMouseEnter={getHover('leftLow', 'Left Low', 13, 21)}
              onClick={getHover('leftLow', 'Left Low', 13, 21)}
              onMouseLeave={self.clearHover.bind(self)}/>
        <path d="M20.5,31 L31.5,11 L31.5,31 Z" fillOpacity={fillOpacity}
              fill={getColor('leftSlot')} stroke="#444" strokeWidth={strokeWidth}
              onMouseEnter={getHover('leftSlot', 'Left Slot', 27, 28)}
              onClick={getHover('leftSlot', 'Left Slot', 27, 28)}
              onMouseLeave={self.clearHover.bind(self)}/>
        <path d="M31.5,11 L53.5,11 L53.5,21 L31.5,21 Z" fillOpacity={fillOpacity}
              fill={getColor('lowSlot')} stroke="#444" strokeWidth={strokeWidth}
              onMouseEnter={getHover('lowSlot', 'Low Slot', 42.5, 16)}
              onClick={getHover('lowSlot', 'Low Slot', 42.5, 16)}
              onMouseLeave={self.clearHover.bind(self)}/>
        <path d="M31.5,31 L53.5,31 L53.5,21 L31.5,21 Z" fillOpacity={fillOpacity}
              fill={getColor('slot')} stroke="#444" strokeWidth={strokeWidth}
              onMouseEnter={getHover('slot', 'Slot', 42.5, 26)}
              onClick={getHover('slot', 'Slot', 42.5, 26)}
              onMouseLeave={self.clearHover.bind(self)}/>
        <path d="M64.5,31 L53.5,11 L53.5,31 Z" fillOpacity={fillOpacity}
              fill={getColor('rightSlot')} stroke="#444" strokeWidth={strokeWidth}
              onMouseEnter={getHover('rightSlot', 'Right slot', 58, 28)}
              onClick={getHover('rightSlot', 'Right slot', 58, 28)}
              onMouseLeave={self.clearHover.bind(self)}/>
        <path d="M85,11 L85,31 L64.5,31 L53.5,11 Z" fillOpacity={fillOpacity}
              fill={getColor('rightLow')} stroke="#444" strokeWidth={strokeWidth}
              onMouseEnter={getHover('rightLow', 'Right Low', 72, 21)}
              onClick={getHover('rightLow', 'Right Low', 72, 21)}
              onMouseLeave={self.clearHover.bind(self)}/>

        <path d="M0,46 L0,31 L20.5,31 L20.5,46 Z" fillOpacity={fillOpacity}
              fill={getColor('leftOne')} stroke="#444" strokeWidth={strokeWidth}
              onMouseEnter={getHover('leftOne', 'Left One', 10, 39)}
              onClick={getHover('leftOne', 'Left One', 10, 39)}
              onMouseLeave={self.clearHover.bind(self)}/>
        <path d="M20.5,31 L31.5,31 L31.5,46 L20.5,46 Z" fillOpacity={fillOpacity}
              fill={getColor('leftTwo')} stroke="#444" strokeWidth={strokeWidth}
              onMouseEnter={getHover('leftTwo', 'Left Two', 26, 39)}
              onClick={getHover('leftTwo', 'Left Two', 26, 39)}
              onMouseLeave={self.clearHover.bind(self)}/>
        <path d="M31.5,31 L53.5,31 L53.5,46 L31.5,46 Z" fillOpacity={fillOpacity}
              fill={getColor('highSlot')} stroke="#444" strokeWidth={strokeWidth}
              onMouseEnter={getHover('highSlot', 'High Slot', 42.5, 39)}
              onClick={getHover('highSlot', 'High Slot', 42.5, 39)}
              onMouseLeave={self.clearHover.bind(self)}/>
        <path d="M64.5,31 L53.5,31 L53.5,46 L64.5,46 Z" fillOpacity={fillOpacity}
              fill={getColor('rightTwo')} stroke="#444" strokeWidth={strokeWidth}
              onMouseEnter={getHover('rightTwo', 'Right Two', 59, 39)}
              onClick={getHover('rightTwo', 'Right Two', 59, 39)}
              onMouseLeave={self.clearHover.bind(self)}/>
        <path d="M85,46 L85,31 L64.5,31 L64.5,46 Z" fillOpacity={fillOpacity}
              fill={getColor('rightOne')} stroke="#444" strokeWidth={strokeWidth}
              onMouseEnter={getHover('rightOne', 'Right One', 75, 39)}
              onClick={getHover('rightOne', 'Right One', 75, 39)}
              onMouseLeave={self.clearHover.bind(self)}/>

        <path d="M0,46 L0,74 L31.5,74 L31.5,46 Z" fillOpacity={fillOpacity}
              fill={getColor('leftPoint')} stroke="#444" strokeWidth={strokeWidth}
              onMouseEnter={getHover('leftPoint', 'Left Point', 16, 60)}
              onClick={getHover('leftPoint', 'Left Point', 16, 60)}
              onMouseLeave={self.clearHover.bind(self)}/>
        <path d="M31.5,74 L53.5,74 L53.5,46 L31.5,46 Z" fillOpacity={fillOpacity}
              fill={getColor('centerPoint')} stroke="#444" strokeWidth={strokeWidth}
              onMouseEnter={getHover('centerPoint', 'Center Point', 42.5, 60)}
              onClick={getHover('centerPoint', 'Center Point', 42.5, 60)}
              onMouseLeave={self.clearHover.bind(self)}/>
        <path d="M53.5,46 L53.5,74 L85,74 L85,46 Z" fillOpacity={fillOpacity}
              fill={getColor('rightPoint')} stroke="#444" strokeWidth={strokeWidth}
              onMouseEnter={getHover('rightPoint', 'Right Point', 69, 60)}
              onClick={getHover('rightPoint', 'Right Point', 69, 60)}
              onMouseLeave={self.clearHover.bind(self)}/>

        <path d="M0,74 L0,110 L85,110 L85,74 Z" fillOpacity={fillOpacity}
              fill={getColor('long')} stroke="#444" strokeWidth={strokeWidth}
              onMouseEnter={getHover('long', 'Long Shot', 42.5, 78)}
              onClick={getHover('long', 'Long Shot', 42.5, 78)}
              onMouseLeave={self.clearHover.bind(self)}/>

        {showStrokes && (
          <g>
            <text transform="translate(42.5,5.5)" textAnchor="middle" alignmentBaseline="middle"
                  className="zone-label">{getLabel('downLow')}</text>

            <text transform="translate(13,21)" textAnchor="middle" alignmentBaseline="middle"
                  className="zone-label">{getLabel('leftLow')}</text>
            <text transform="translate(27,28)" textAnchor="middle" alignmentBaseline="middle"
                  className="zone-label">{getLabel('leftSlot')}</text>
            <text transform="translate(42.5,16)" textAnchor="middle" alignmentBaseline="middle"
                  className="zone-label">{getLabel('lowSlot')}</text>
            <text transform="translate(42.5,26)" textAnchor="middle" alignmentBaseline="middle"
                  className="zone-label">{getLabel('slot')}</text>
            <text transform="translate(58,28)" textAnchor="middle" alignmentBaseline="middle"
                  className="zone-label">{getLabel('rightSlot')}</text>
            <text transform="translate(72,21)" textAnchor="middle" alignmentBaseline="middle"
                  className="zone-label">{getLabel('rightLow')}</text>

            <text transform="translate(10,39)" textAnchor="middle" alignmentBaseline="middle"
                  className="zone-label">{getLabel('leftOne')}</text>
            <text transform="translate(26,39)" textAnchor="middle" alignmentBaseline="middle"
                  className="zone-label">{getLabel('leftTwo')}</text>
            <text transform="translate(42.5,39)" textAnchor="middle" alignmentBaseline="middle"
                  className="zone-label">{getLabel('highSlot')}</text>
            <text transform="translate(59,39)" textAnchor="middle" alignmentBaseline="middle"
                  className="zone-label">{getLabel('rightTwo')}</text>
            <text transform="translate(75,39)" textAnchor="middle" alignmentBaseline="middle"
                  className="zone-label">{getLabel('rightOne')}</text>

            <text transform="translate(16,60)" textAnchor="middle" alignmentBaseline="middle"
                  className="zone-label">{getLabel('leftPoint')}</text>
            <text transform="translate(42.5,60)" textAnchor="middle" alignmentBaseline="middle"
                  className="zone-label">{getLabel('centerPoint')}</text>
            <text transform="translate(69,60)" textAnchor="middle" alignmentBaseline="middle"
                  className="zone-label">{getLabel('rightPoint')}</text>

            <text transform="translate(42.5,78)" textAnchor="middle" alignmentBaseline="middle"
                  className="zone-label">{getLabel('long')}</text>
          </g>
        )}
      </g>
    )
  }

  createShapes() {
    switch (this.props.shotType) {
      case 'shots':
        return this.createShots();
      case 'circles':
        return this.createCircles();
      case 'zones':
        return this.createZones(true);
      case 'hexshots':
        return (
          <g>
            <g>{this.createOutlines()}</g>
            <g>{this.createShots()}</g>
          </g>
        );
      case 'zoneshots':
        return (
          <g>
            <g>{this.createZones()}</g>
            <g>{this.createShots()}</g>
          </g>
        );
      case 'bins':
      default:
        return this.createBins();
    }
  }

  render() {
    // const redLines = '#C88484';
    const redLines = '#ccc';

    return (
      <div ref="wrapper" className="HockeyShotChart">
        <Resize handleWidth handleHeight onResize={this.handelResize.bind(this)}/>
        <div className="wrapper" style={{height: this.state.height + 'px'}}>
          <svg ref="svg" width={this.state.width} height={this.state.height} viewBox="0 0 86 110">
            <g transform="translate(.5,.5)" height="110" width="85">

              <path d="M0,110 L0,26 A26,26 0 0,1 26,0 L59,0 A26,26 0 0,1 85,26 L85,110 L0,110 Z"
                    stroke="#666" strokeWidth=".6" fill="#f8f8f8"/>

              <path d="M5,11 L80,11" stroke={redLines} strokeWidth=".4"/>

              <path d="M28.5,0 L31.5,11" stroke={redLines} strokeWidth=".4"/>
              <path d="M56.5,0 L53.5,11" stroke={redLines} strokeWidth=".4"/>

              <path d="M38.5,11 L38.5,15.5 Q42.5,18 46.5,15.5 L46.5,11 Z"
                    stroke={redLines} strokeWidth=".4" fill="#eee"/>

              <path d="M39.5,11 L39.5,8.8 A1,1 0 0,1 40.5,7.8 L44.5,7.8 A1,1 0 0,1 45.5,8.8 L45.5,11 Z"
                    stroke={redLines} strokeWidth=".4" fill="#eee"/>

              <circle cx="20.5" cy="31" r="15" stroke={redLines} strokeWidth=".4" fill="none"/>
              <circle cx="20.5" cy="31" r="1" fill={redLines} stroke="none"/>

              <path d="M16.5,29 L19.5,29 L19.5,25" stroke={redLines} strokeWidth=".4" fill="none"/>
              <path d="M24.5,29 L21.5,29 L21.5,25" stroke={redLines} strokeWidth=".4" fill="none"/>
              <path d="M16.5,33 L19.5,33 L19.5,37" stroke={redLines} strokeWidth=".4" fill="none"/>
              <path d="M24.5,33 L21.5,33 L21.5,37" stroke={redLines} strokeWidth=".4" fill="none"/>

              <path d="M3.5,28.2 L5.6,28.2" stroke={redLines} strokeWidth=".4" fill="none"/>
              <path d="M3.5,33.8 L5.6,33.8" stroke={redLines} strokeWidth=".4" fill="none"/>
              <path d="M35.4,28.2 L37.5,28.2" stroke={redLines} strokeWidth=".4" fill="none"/>
              <path d="M35.4,33.8 L37.5,33.8" stroke={redLines} strokeWidth=".4" fill="none"/>

              <circle cx="64.5" cy="31" r="15" stroke={redLines} strokeWidth=".4" fill="none"/>
              <circle cx="64.5" cy="31" r="1" fill={redLines} stroke="none"/>

              <path d="M60.5,29 L63.5,29 L63.5,25" stroke={redLines} strokeWidth=".4" fill="none"/>
              <path d="M68.5,29 L65.5,29 L65.5,25" stroke={redLines} strokeWidth=".4" fill="none"/>
              <path d="M60.5,33 L63.5,33 L63.5,37" stroke={redLines} strokeWidth=".4" fill="none"/>
              <path d="M68.5,33 L65.5,33 L65.5,37" stroke={redLines} strokeWidth=".4" fill="none"/>

              <path d="M47.5,28.2 L49.6,28.2" stroke={redLines} strokeWidth=".4" fill="none"/>
              <path d="M47.5,33.8 L49.6,33.8" stroke={redLines} strokeWidth=".4" fill="none"/>
              <path d="M79.4,28.2 L81.5,28.2" stroke={redLines} strokeWidth=".4" fill="none"/>
              <path d="M79.4,33.8 L81.5,33.8" stroke={redLines} strokeWidth=".4" fill="none"/>

              <path d="M0,74 L85,74" stroke="#ccc" strokeWidth="1.3"/>

              <circle cx="20.5" cy="80" r="1.2" fill={redLines} stroke="none"/>
              <circle cx="64.5" cy="80" r="1.2" fill={redLines} stroke="none"/>

              <path d="M27.5,100 A15,15 0 0,1 57.5,100" stroke={redLines} strokeWidth=".4" fill="none"/>

              <g className="shapes">
                {this.createShapes()}
              </g>

              <path fill="#fff"
                    d="M-1,-1 L-1,111 L86,111 L86,-1 Z M0,110 L0,26 A26,26 0 0,1 26,0 L59,0 A26,26 0 0,1 85,26 L85,110 L0,110 Z "/>

              <path d="M0,110 L0,26 A26,26 0 0,1 26,0 L59,0 A26,26 0 0,1 85,26 L85,110 L0,110 Z"
                    stroke="#444" strokeWidth=".6" fill="none"/>
            </g>
          </svg>
          <svg className="legend" ref="legend" width={this.state.width} viewBox="0 0 86 10">
            <g className="legend" height="10" width="85" transform="translate(.5,0)">
              <rect width="84.4" height="10" fill="#e9e9e9" opacity="0.8" transform="translate(.3,0)"/>

              {_.includes(['shots', 'hexshots', 'zoneshots'], this.props.shotType) && (
                <g transform="translate(4,5)" height="10" width="20">
                  <circle cy="-1.3" cx="0" r=".6" fill="#999"/>
                  <circle cy="1.3" cx="0" r=".6" fill="#2980b9"/>
                  <text transform="translate(1.5,-1.2)" alignmentBaseline="middle">Shot Location</text>
                  <text transform="translate(1.5,1.4)" alignmentBaseline="middle">Goal Location</text>
                </g>
              )}

              {this.props.shotType === 'bins' && (
                <g transform="translate(4,5.3)" height="10" width="20">
                  <text transform="translate(4.6,-2.7)" textAnchor="middle">Shot Frequency</text>
                  <path transform="translate(0,0)" fill={heatColors[0]}
                        d="m0,-0.817l0.708,0.408l0,0.817l-0.708,0.408l-0.708,-0.408l0,-0.817z"/>
                  <path transform="translate(2.1,0)" fill={heatColors[0]}
                        d="m0,-1.226l1.062,0.613l0,1.226l-1.062,0.613l-1.062,-0.613l0,-1.226z"/>
                  <path transform="translate(4.93,0)" fill={heatColors[0]}
                        d="m0,-1.635l1.416,0.817l0,1.635l-1.416,0.817l-1.416,-0.817l0,-1.635z"/>
                  <path transform="translate(8.5,0)" fill={heatColors[0]}
                        d="m0,-2.044l1.770,1.022l0,2.0442l-1.770,1.0224l-1.770,-1.022l0,-2.044z"/>
                  <text transform="translate(0,0.12)" textAnchor="middle"
                        alignmentBaseline="middle" className="pm">-
                  </text>
                  <text transform="translate(8.5,0.15)" textAnchor="middle"
                        alignmentBaseline="middle" className="pm">+
                  </text>
                </g>
              )}

              {(this.props.shotType === 'bins' || this.props.shotType === 'hexshots') && (
                <g transform="translate(70,5)" height="10" width="20">
                  <text transform="translate(4.8,-2.4)" textAnchor="middle">Shot Efficiency</text>
                  <text transform="translate(4.8,3)" textAnchor="middle" className="small">AVG</text>
                  <path transform="translate(0,0)"
                        d="m0,-1.635l1.416,0.817l0,1.635l-1.416,0.817l-1.416,-0.817l0,-1.635z"
                        fill={heatColors[0]}/>
                  <path transform="translate(3.2,0)"
                        d="m0,-1.635l1.416,0.817l0,1.635l-1.416,0.817l-1.416,-0.817l0,-1.635z"
                        fill={heatColors[1]}/>
                  <path transform="translate(6.4,0)"
                        d="m0,-1.635l1.416,0.817l0,1.635l-1.416,0.817l-1.416,-0.817l0,-1.635z"
                        fill={heatColors[2]}/>
                  <path transform="translate(9.6,0)"
                        d="m0,-1.635l1.416,0.817l0,1.635l-1.416,0.817l-1.416,-0.817l0,-1.635z"
                        fill={heatColors[3]}/>
                  <text transform="translate(0,0.15)" textAnchor="middle"
                        alignmentBaseline="middle" className="pm">-
                  </text>
                  <text transform="translate(9.6,0.15)" textAnchor="middle"
                        alignmentBaseline="middle" className="pm">+
                  </text>
                </g>
              )}

              {this.props.shotType === 'circles' && (
                <g transform="translate(4,5.3)" height="10" width="20">
                  <text transform="translate(4.6,-2.7)" textAnchor="middle">Shot Frequency</text>
                  <circle cx="0" cy="0" r=".75" fill={heatColors[0]}/>
                  <circle cx="2.1" cy="0" r="1.1" fill={heatColors[0]}/>
                  <circle cx="4.93" cy="0" r="1.45" fill={heatColors[0]}/>
                  <circle cx="8.5" cy="0" r="1.8" fill={heatColors[0]}/>

                  <text transform="translate(0,0.12)" textAnchor="middle"
                        alignmentBaseline="middle" className="pm">-
                  </text>
                  <text transform="translate(8.5,0.15)" textAnchor="middle"
                        alignmentBaseline="middle" className="pm">+
                  </text>
                </g>
              )}

              {_.includes(['circles', 'zones', 'zoneshots'], this.props.shotType) && (
                <g transform="translate(70,5)" height="10" width="20">
                  <text transform="translate(4.8,-2.4)" textAnchor="middle">Shot Efficiency</text>
                  <text transform="translate(4.8,3)" textAnchor="middle" className="small">AVG</text>
                  <circle cx="0" cy="0" r="1.45" fill={heatColors[0]}/>
                  <circle cx="3.2" cy="0" r="1.45" fill={heatColors[1]}/>
                  <circle cx="6.4" cy="0" r="1.45" fill={heatColors[2]}/>
                  <circle cx="9.6" cy="0" r="1.45" fill={heatColors[3]}/>

                  <text transform="translate(0,0.15)" textAnchor="middle"
                        alignmentBaseline="middle" className="pm">-
                  </text>
                  <text transform="translate(9.6,0.15)" textAnchor="middle"
                        alignmentBaseline="middle" className="pm">+
                  </text>
                </g>
              )}

              <text transform="translate(42.5,6)" textAnchor="middle" className="title">2014-2016 Shots</text>

              <rect y="9.4" width="85" height=".6" fill="#444"/>
            </g>
          </svg>
        </div>
        <HoverOver data={this.state.hover}/>
      </div>
    );
  }
}

export default HockeyShotChart;


// WEBPACK FOOTER //
// ./src/components/HockeyShotChart.js